import { CUSTOMER_SECURITY_MASTER_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useCustomers, useCustomersByName } from '@src/hooks/useCustomer';
import { useCustomerSecuritiesBySymbol, useCustomerSecurityRows } from '@src/providers';
import {
  ACTION,
  CUSTOMER_SECURITY,
  EntityAdmin,
  InstrumentCompositionEnum,
  ModeEnum,
  useBasePairFilter,
  useEntityAdminWorkflow,
  useSecuritiesContext,
  useSymbolCurrencyFilter,
  useSymbolsFilter,
  useUnifiedLiquidityContext,
  type CustomerSecurity,
} from '@talos/kyoko';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { CreateCustomSecurityWorkflow } from './CreateCustomSecurityWorkflow';
import {
  getCustomerSecurityDeletePath,
  getCustomerSecurityEntityForPatchPost,
  getCustomerSecurityGroupColumnDisplayName,
  getCustomerSecurityMasterColumns,
  getCustomerSecurityMasterDrawerOptions,
  isCustomerSecurityChildRow,
  priceViaSyntheticCrossFilter,
  type CustomerSecurityDrawerRecord,
} from './utils';

const WS_REQUEST = { name: CUSTOMER_SECURITY, tag: 'CustomerSecurityMaster' };

export const CustomerSecurityMaster = () => {
  const { isAuthorized } = useRoleAuth();
  const { isLoaded: isSecuritiesLoaded, securitiesList } = useSecuritiesContext();
  const { startWorkflowAction } = useEntityAdminWorkflow(CreateCustomSecurityWorkflow, {
    disabled: !isSecuritiesLoaded,
  });
  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const {
    customerSecurityAllowedUnderlyingProductTypes,
    showSecurityMasterFillIncrementColumns,
    showSecurityMasterUnifiedLiquidityColumn,
    showSecurityMasterUseUnderlyingSecurityForRFQ,
    unifiedQuoteCurrencyGroups,
    enableCreateCustomSecurity,
    enableCounterpartyOverrideCustomerSecurityMaster,
  } = useFeatureFlag();
  const customerSecuritiesBySymbol = useCustomerSecuritiesBySymbol();
  const customerSecurityRows = useCustomerSecurityRows();
  const customers = useCustomers();
  const customersByName = useCustomersByName();
  const canEditDealer = isAuthorized(ACTION.EDIT_DEALER);
  const securitiesWithoutSyntheticComposition = useMemo(() => {
    return securitiesList.filter(
      // Don't allow users to create CustomerSecurities tied to Synthetic Securities
      security => security.Composition !== InstrumentCompositionEnum.Synthetic
    );
  }, [securitiesList]);

  const symbolsFilter = useSymbolsFilter(securitiesWithoutSyntheticComposition);
  const currencyBaseQuoteOfSymbolFilter = useSymbolCurrencyFilter(securitiesWithoutSyntheticComposition);
  const basePairFilter = useBasePairFilter(securitiesWithoutSyntheticComposition);
  const availableFilterableProperties = useMemo(() => {
    return [symbolsFilter, currencyBaseQuoteOfSymbolFilter, basePairFilter, priceViaSyntheticCrossFilter];
  }, [currencyBaseQuoteOfSymbolFilter, basePairFilter, symbolsFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_SECURITY_MASTER_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  const drawerOptions = useMemo(() => {
    if (unifiedLiquidityTokenBySymbol == null || customerSecuritiesBySymbol == null) {
      return undefined;
    }
    return getCustomerSecurityMasterDrawerOptions({
      allSecurities: securitiesList,
      filteredSecuritiesForSymbolDropdown: securitiesWithoutSyntheticComposition,
      unifiedLiquidityTokenBySymbol,
      allowedProductTypes: customerSecurityAllowedUnderlyingProductTypes,
      showSecurityMasterUnifiedLiquidityColumn,
      showSecurityMasterFillIncrementColumns,
      showSecurityMasterUseUnderlyingSecurityForRFQ,
      unifiedQuoteCurrencyGroups,
      customers,
      customerSecurityRows,
    });
  }, [
    unifiedLiquidityTokenBySymbol,
    customerSecuritiesBySymbol,
    customerSecurityRows,
    customers,
    securitiesList,
    securitiesWithoutSyntheticComposition,
    customerSecurityAllowedUnderlyingProductTypes,
    showSecurityMasterUnifiedLiquidityColumn,
    showSecurityMasterFillIncrementColumns,
    showSecurityMasterUseUnderlyingSecurityForRFQ,
    unifiedQuoteCurrencyGroups,
  ]);

  const columns = useMemo(
    () =>
      getCustomerSecurityMasterColumns(
        showSecurityMasterFillIncrementColumns,
        showSecurityMasterUnifiedLiquidityColumn,
        showSecurityMasterUseUnderlyingSecurityForRFQ,
        enableCounterpartyOverrideCustomerSecurityMaster
      ),
    [
      showSecurityMasterFillIncrementColumns,
      showSecurityMasterUnifiedLiquidityColumn,
      showSecurityMasterUseUnderlyingSecurityForRFQ,
      enableCounterpartyOverrideCustomerSecurityMaster,
    ]
  );

  const panelActions = useMemo(() => {
    return enableCreateCustomSecurity && canEditDealer ? [startWorkflowAction] : [];
  }, [canEditDealer, enableCreateCustomSecurity, startWorkflowAction]);

  const groupColumnDef: ColDef<CustomerSecurity> = useMemo(
    () => ({
      headerName: 'Symbol - Customer',
      pinned: 'left' as const,
      valueFormatter: ({ data }: ValueFormatterParams<CustomerSecurity>) =>
        getCustomerSecurityGroupColumnDisplayName(data, customersByName),
    }),
    [customersByName]
  );

  return (
    <EntityAdmin<CustomerSecurity, CustomerSecurityDrawerRecord>
      title="Security Master"
      entityName="Symbol"
      childEntityName="Counterparty Override"
      wsRequest={WS_REQUEST}
      entityIDField="Symbol"
      childIDField={enableCounterpartyOverrideCustomerSecurityMaster ? 'Counterparty' : undefined}
      userFilterFunc={entity => enableCounterpartyOverrideCustomerSecurityMaster || !isCustomerSecurityChildRow(entity)}
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      persistKey="dealer/secmaster"
      path="/customer-securities"
      getPatchPath={() => '/customer-securities'}
      getDeletePath={getCustomerSecurityDeletePath}
      getEntityForPatchPost={getCustomerSecurityEntityForPatchPost}
      allowModeSwitch={{
        isDisabled: () => !canEditDealer,
        isHidden: isCustomerSecurityChildRow,
      }}
      allowEditEntity={canEditDealer}
      allowBulkEdit={canEditDealer}
      bulkEditFields={['DefaultPricingRuleMode', 'PriceViaSyntheticCross']}
      isRowBulkEditSelectable={entity => !isCustomerSecurityChildRow(entity)}
      allowDeleteEntity={
        canEditDealer && {
          isHidden: entity => !isCustomerSecurityChildRow(entity),
          isDisabled: entity => !isCustomerSecurityChildRow(entity),
        }
      }
      allowAddEntity={{
        main: canEditDealer,
        child: canEditDealer,
      }}
      panelActions={panelActions}
      columns={columns}
      groupColumnDef={groupColumnDef}
      getEntityDrawerOptions={(entity, addingChildEntity) =>
        addingChildEntity || (entity != null && isCustomerSecurityChildRow(entity))
          ? drawerOptions?.child
          : drawerOptions?.parent
      }
      removeNullValuesFromPatchPayload={true}
      newEntityDefault={() => ({
        Mode: ModeEnum.Enabled,
        DefaultPricingRuleMode: ModeEnum.Enabled,
      })}
    />
  );
};
